import { RepresentationData } from '@ardoq/data-model';
import { VOID_REPRESENTATION_DATA } from '@ardoq/global-consts';
import {
  ComponentGraphGroup,
  GraphInterface,
  LabelsInterface,
} from '@ardoq/graph';
import { getIcons } from '@ardoq/icons';
import type { ModernizedBlockDiagramViewSettings } from 'tabview/blockDiagram/types';
import { isPureGraphNodeOrGroup } from 'yfilesExtensions/styles/modernized/leafNode/util';

/**
 * Safely extracts representation data from an INode tag.
 * For open component groups, returns null if there's no displayable content (icon or image).
 * For other nodes/groups, returns the representation data directly.
 */
export const safeGetRepresentationDataFromINodeTag = (
  tag: any
): RepresentationData | null => {
  // Check if tag has getRepresentationData method
  if (!isPureGraphNodeOrGroup(tag)) {
    return null;
  }

  const representationData = tag.getRepresentationData();
  if (!representationData) {
    return null;
  }

  // For open component groups, only return data if there's displayable content
  // we only show images or icons in the open component group header
  if (tag.isGroup() && !(tag as ComponentGraphGroup).collapsed) {
    const hasDisplayableContent = Boolean(
      (representationData.isImage && representationData.value) ||
      representationData.icon
    );
    return hasDisplayableContent ? representationData : null;
  }

  // For other nodes/groups, return data directly
  return representationData;
};

const resolve = (
  image: string | null,
  icon: string | null,
  shape: string | null,
  componentStyle: ModernizedBlockDiagramViewSettings['componentStyle']
) => {
  if (image) {
    return { isImage: true, value: image };
  }
  if (icon && componentStyle === 'icon') {
    return { isImage: false, value: icon, icon: getIcons()[icon] };
  }
  if (shape && componentStyle === 'shape') {
    return {
      isImage: false,
      value: null,
      shapeName: shape,
      icon: icon ? getIcons()[icon] : undefined,
    };
  }
  if (icon || shape) {
    return {
      isImage: false,
      value: icon,
      shapeName: shape,
      icon: icon ? getIcons()[icon] : undefined,
    };
  }
  return null;
};

const getModernizedBlockDiagramRepresentationData = (
  viewInterface: LabelsInterface & GraphInterface,
  componentId: string,
  componentStyle: ModernizedBlockDiagramViewSettings['componentStyle']
): RepresentationData => {
  const componentImage = viewInterface.getComponentFieldValue(
    componentId,
    'image'
  ) as string | null;
  const componentIcon = viewInterface.getComponentFieldValue(
    componentId,
    'icon'
  ) as string | null;
  const componentShape = viewInterface.getComponentFieldValue(
    componentId,
    'shape'
  ) as string | null;

  // If component has image, use it directly
  if (componentImage) {
    return resolve(componentImage, null, null, componentStyle)!;
  }

  // If component has icon, use it with component's shape
  if (componentIcon) {
    return resolve(null, componentIcon, componentShape, componentStyle)!;
  }

  // Component has neither image nor icon - fall back to component type defaults
  const componentType = viewInterface.getComponentType(componentId);

  if (!componentType) {
    // No type available - use component shape if it exists, otherwise return void
    return componentShape
      ? resolve(null, null, componentShape, componentStyle)!
      : VOID_REPRESENTATION_DATA;
  }

  // Use component type's image or icon (like Backbone does: ct.image || ct.icon)
  const fallbackIcon = componentType.image || componentType.icon;
  const fallbackShape = componentShape || componentType.shape;

  return (
    resolve(componentType.image, fallbackIcon, fallbackShape, componentStyle) ??
    VOID_REPRESENTATION_DATA
  );
};
export { getModernizedBlockDiagramRepresentationData };
